import React, { Fragment, useEffect, useState } from 'react'
import { Edit, Delete, Visibility } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { Modal } from '../../components/_EOS/Modal'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'

import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useModal } from '../../hooks/useModal'
import { useSuppliers } from '../../hooks/useSuppliers'

import { filterOptions } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier
} from './config/api'
import { SupplierForm } from './components/SupplierForm'
import { ViewSupplier } from './components/ViewSupplier'

import { Supplier } from '../../interfaces/Supplier/Supplier'
import { Filter } from '../../components/_WMS/FilterComponent/types'
import { Status } from '../../hooks/useFetchState'
import { ApiResponse } from '../../typings/ApiResponse'
import { GenericOrderBy } from '../../components/_EOS/Table/types'
import { useProductOrigins } from '../../hooks'

export const SupplierPage: React.FC = () => {
  const { callApi, state } = useApi<ApiResponse<Supplier>>()
  const pagination = usePagination(10)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const { getSuppliers: refreshSuppliers } = useSuppliers()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  // Estado de ordenamiento
  const [orderBy, setOrderBy] = useState<GenericOrderBy<Supplier>>({
    field: 'supplierName' as keyof Supplier,
    direction: 'asc'
  })

  const {
    productOriginsState: { productOrigins },
    getProductOrigins
  } = useProductOrigins()
  const createModal = useModal('Crear Proveedor')
  const editModal = useModal<Supplier>('Editar Proveedor')
  const deleteModal = useModal<Supplier>('Confirmar Eliminación')
  const viewModal = useModal<Supplier>('Información del Proveedor')

  const columnsConfig = getColumnsConfig(productOrigins)

  const requestData = (filters: Filter[], page: number, pageSize: number) => {
    callApi(getSuppliers(filters, page, pageSize, orderBy)).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const handleSorting = (field: keyof Supplier, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleFilterChange = (value: React.SetStateAction<Filter[]>) => {
    let newFilters: Filter[]

    if (typeof value === 'function') {
      newFilters = value(selectedFilters)
    } else {
      newFilters = value
    }

    setSelectedFilters(newFilters)

    // Si se limpiaron todos los filtros, buscar automáticamente
    if (newFilters.length === 0 && selectedFilters.length > 0) {
      requestData([], pagination.page, pagination.pageSize)
    }
  }

  const handleCreate = (values: any) => {
    callApi(createSupplier(values))
      .then(() => {
        openSnackBar('Proveedor creado correctamente', true)
        createModal.closeModal()
      })
      .catch((error) => {
        openSnackBar('Error al crear proveedor: ' + error, false)
      })
      .finally(() => {
        requestData(selectedFilters, pagination.page, pagination.pageSize)
      })
  }

  const handleEdit = (values: Supplier) => {
    callApi(updateSupplier(values.id, values))
      .then(() => {
        openSnackBar('Proveedor actualizado correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        refreshSuppliers()
        editModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleDelete = (id: number) => {
    callApi(deleteSupplier(id))
      .then(() => {
        openSnackBar('Proveedor eliminado correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        refreshSuppliers()
        deleteModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleCreateClick = () => {
    createModal.openModal('md')
  }

  useEffect(() => {
    getProductOrigins()
  }, [])

  useEffect(() => {
    // Solo buscar automáticamente en la carga inicial, no cuando cambian los filtros
    requestData([], pagination.page, pagination.pageSize)
  }, [pagination.pageSize])

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestData(selectedFilters, pagination.page, pagination.pageSize)
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage
        pageTitle='Gestionar Proveedor'
        buttonAction={handleCreateClick}
        buttonTitle='Crear Proveedor'
      >
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={() =>
            requestData(selectedFilters, pagination.page, pagination.pageSize)
          }
          selectedFilters={selectedFilters}
          setSelectedFilters={handleFilterChange}
        />

        <GenericTableV2<Supplier>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='id'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof Supplier,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('md', item),
              icon: <Edit />
            },
            {
              key: 'view',
              label: 'Ver',
              onClick: (item) => viewModal.openModal('md', item),
              icon: <Visibility />
            },
            {
              key: 'delete',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
      >
        <SupplierForm onSubmit={handleCreate} />
      </Modal>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
      >
        <SupplierForm
          initialData={editModal.extendedProps}
          onSubmit={handleEdit}
          isEditing={true}
        />
      </Modal>

      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        loading={state.status === Status.Loading}
        successButton={{
          action: () => handleDelete(deleteModal.extendedProps?.id || 0),
          title: 'Confirmar'
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
      >
        ¿Desea eliminar el proveedor{' '}
        <strong>{deleteModal.extendedProps?.supplierName}</strong>?
      </Modal>

      <Modal
        open={viewModal.isOpen}
        maxWidth={viewModal.size}
        title={viewModal.title}
        onClose={viewModal.closeModal}
      >
        <ViewSupplier supplier={viewModal.extendedProps} />
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
